import {useRef, useEffect, useLayoutEffect, useState, useCallback} from "react";

/**
 * Custom hook to manage auto-scrolling behavior for a chat interface.
 * Returns an object containing:
 *  - scrollRef: Ref to attach to the scrollable element.
 *  - isInitial: Boolean state indicating if it's the initial render phase.
 *  - showScrollDownButton: Boolean state indicating if the scroll-down button should be shown.
 *  - forceScrollToBottom: Function to manually trigger a scroll to the bottom and re-enable auto-scroll.
 */
export function useChatScroll<T>(
    dependencies: T[],
    options?: {disableAutoScroll?: boolean; isStreaming?: boolean}
) {
    const ref = useRef<HTMLDivElement>(null);
    const [messages] = dependencies;
    const hasNoMessages = Array.isArray(messages) && messages.length === 0;
    // Use a ref to track auto-scroll state without causing re-renders
    const shouldAutoScroll = useRef(!options?.disableAutoScroll);
    // Keep ref in sync when option changes
    if (options?.disableAutoScroll) {
        shouldAutoScroll.current = false;
    }
    // Track streaming state to detect the moment a response finishes.
    const wasStreaming = useRef(!!options?.isStreaming);
    // Ref to store the previous scrollHeight to detect content additions
    const previousScrollHeight = useRef<number | null>(null);
    // State for scroll down button visibility
    const [showScrollDownButton, setShowScrollDownButton] = useState(false);
    // Re-introduce isInitial state
    const [isInitial, setIsInitial] = useState(true);

    // Function to force scroll to bottom
    const forceScrollToBottom = useCallback(() => {
        const element = ref.current;
        if (element) {
            shouldAutoScroll.current = true; // Re-enable auto-scroll
            element.scrollTo({top: element.scrollHeight, behavior: "smooth"});
        }
    }, []); // Depends only on the ref container

    // Effect for initial positioning (runs once, sync)
    useLayoutEffect(() => {
        const element = ref.current;
        if (element && !options?.disableAutoScroll && !hasNoMessages) {
            element.scrollTop = element.scrollHeight;
        }
    }, []);

    useEffect(() => {
        const element = ref.current;
        if (!element || hasNoMessages) return;

        // When streaming finishes, the message is finalized and the references
        // block mounts in the same commit, growing the content. Stop following
        // the bottom so the view stays put and the user can read from the top.
        const streamingJustEnded =
            wasStreaming.current && !options?.isStreaming;
        wasStreaming.current = !!options?.isStreaming;

        // Handle scrolling based on initial state and auto-scroll preference
        if (isInitial) {
            if (!options?.disableAutoScroll) {
                // Instant scroll for the initial load of dependencies
                element.scrollTop = element.scrollHeight;
            }
            // Transition out of initial state immediately after the first dependency update
            setIsInitial(false);
        } else if (streamingJustEnded) {
            shouldAutoScroll.current = false;
        } else if (shouldAutoScroll.current) {
            // Smooth scroll for subsequent updates if auto-scroll is enabled
            element.scrollTo({
                top: element.scrollHeight,
                behavior: "smooth",
            });
        }

        // Update scroll height for next effect run (useful for detecting content changes if needed later)
        previousScrollHeight.current = element.scrollHeight;

        // Removed the previous isInitial check with setTimeout
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...dependencies, options?.isStreaming]); // Dependencies for updates

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const handleScroll = () => {
            // Check if scrolled near the bottom (e.g., within 20px)
            const isAtBottom =
                element.scrollHeight -
                    element.scrollTop -
                    element.clientHeight <
                20;
            shouldAutoScroll.current = isAtBottom;

            // Check if scrolled up significantly (e.g., more than 100px from bottom)
            const isScrolledUp =
                element.scrollHeight -
                    element.scrollTop -
                    element.clientHeight >=
                100;
            setShowScrollDownButton(isScrolledUp);
        };

        element.addEventListener("scroll", handleScroll);

        // Cleanup
        return () => {
            element.removeEventListener("scroll", handleScroll);
        };
    }, [ref, dependencies]); // Only depends on the ref

    // Return ref, states, and the new function
    return {
        scrollRef: ref,
        isInitial,
        showScrollDownButton,
        forceScrollToBottom,
    };
}
